import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'next-i18next'

import TestimonialsSection from '~/start/components/Testimonials'
import StartAnalytics from '~/analytics/start/startAnalytics'
import { landing } from '~/start/constants'
import MindSection from '~/landing/components/MindSection'
import { Button } from '~/components/Button'
import AtomicMath from '~/landing/components/AtomicMath'
import KnowledgeGaps from '~/landing/components/KnowledgeGaps'
import BgPattern from '~/landing/components/BgPattern'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { Breakpoint, useCheckBreakpoint } from '~/utils/useBreakpoint'
import MindSectionMobile from '~/landing/components/MindSectionMobile'

import BaseBuildBurn from '~/landing/components/BaseBuildBurn'
import MathRecipe from '~/landing/components/MathRecipe'
import FAQ from '~/start/components/FAQ'
import { GET_STARTED_ENTRY_ROUTE } from '~/get-started/entryRoute.const'
import useGetStartedEntryRoute from '~/get-started/hooks/useGetStartedEntryRoute'
import { PartnerDetailsType } from '~/campaigns/types/campaign.types'
import { LandingEntryRouteContext } from '~/landing/contexts/landingEntryRoute'

import Sponsers from './components/v2/Sponsers'
import IntroSection from './components/IntroSection'

interface LandingPageProps {
  hideFaq?: boolean
  isMAWCampaign?: boolean
  /** Co-brands the hero with a partner logo. */
  partnerDetails?: PartnerDetailsType
  /** Overrides where every sign-up CTA on the page goes. */
  entryRoute?: string
}

export default function LandingPage({
  isMAWCampaign = false,
  hideFaq = false,
  partnerDetails,
  entryRoute: entryRouteProp = GET_STARTED_ENTRY_ROUTE,
}: LandingPageProps): React.ReactElement {
  const { t } = useTranslation('landing')
  const entryRoute = useGetStartedEntryRoute(entryRouteProp)
  const isDesktop = useCheckBreakpoint({ gt: Breakpoint.MD })

  const introRef = useRef<HTMLDivElement>(null)
  const meetMindRef = useRef<HTMLDivElement>(null)
  const testimonialsRef = useRef<HTMLDivElement>(null)
  const baseBuildBurnRef = useRef<HTMLDivElement>(null)
  const mathRecipeRef = useRef<HTMLDivElement>(null)
  const atomicMathRef = useRef<HTMLDivElement>(null)
  const faqRef = useRef<HTMLDivElement>(null)
  const knowledgeGapsRef = useRef<HTMLDivElement>(null)

  const sectionRefs = useMemo(
    () => ({
      intro: introRef,
      meetMind: meetMindRef,
      testimonials: testimonialsRef,
      baseBuildBurn: baseBuildBurnRef,
      mathRecipe: mathRecipeRef,
      atomicMath: atomicMathRef,
      faq: faqRef,
      knowledgeGaps: knowledgeGapsRef,
    }),
    [],
  )

  function trackSectionView(section: keyof typeof landing): void {
    StartAnalytics.logSectionView(landing[section].nr, landing[section].name)
  }

  useEffect(() => {
    const seen = new Set<string>()
    const handleScroll = (): void => {
      Object.entries(sectionRefs).forEach(([key, ref]) => {
        if (ref.current && !seen.has(key)) {
          const rect = ref.current.getBoundingClientRect()
          if (rect.top < window.innerHeight * 0.5 && rect.bottom > 0) {
            trackSectionView(key as keyof typeof landing)
            seen.add(key)
          }
        }
      })
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [sectionRefs])

  const handleButtonClick = (): void => {
    StartAnalytics.logCtaClick('onboarding_cta', 'landing_section_3_cta')
  }

  const containerRef = useRef<HTMLDivElement>(null)
  // This is a temporary solution to scroll to top when clicking the CTA button in the intro section for the Make A Wish campaign, as the registration form is embedded in the landing page and we want to ensure that users see it when they click the CTA. This can be removed once the registration form is moved to a separate page.
  useEffect(() => {
    if (!isMAWCampaign || !containerRef.current) return

    const handler = (e: MouseEvent): void => {
      const anchor = (e.target as HTMLElement).closest('a')
      if (!anchor) return
      const href = anchor.getAttribute('href') || ''
      if (href === entryRoute || href.endsWith(entryRoute)) {
        e.preventDefault()
        e.stopImmediatePropagation()
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }

    const el = containerRef.current
    el.addEventListener('click', handler, true)
    return () => el.removeEventListener('click', handler, true)
  }, [isMAWCampaign, entryRoute])

  const onArrowClick = (): void => {
    const videoSection = sectionRefs.meetMind.current

    if (videoSection) {
      videoSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <LandingEntryRouteContext.Provider value={entryRoute}>
      <div className="flex flex-col bg-purple-darker w-full min-h-screen overflow-hidden" ref={containerRef}>
        <div ref={sectionRefs.intro}>
          <IntroSection
            entryRoute={entryRoute}
            isMAWCampaign={isMAWCampaign}
            onArrowClick={onArrowClick}
            partnerDetails={partnerDetails}
          />
        </div>
        <Sponsers />
        <div className="overflow-hidden w-screen" ref={sectionRefs.meetMind}>
          {isDesktop ? <MindSection /> : <MindSectionMobile />}
        </div>
        <div
          className="relative flex flex-col justify-center items-center bg-gradient-to-b from-purple-indigo to-purple-plum pb-8"
          ref={sectionRefs.testimonials}
        >
          <div className="w-screen">
            <BgPattern />
            <ResponsiveImage
              className="absolute min-w-196 w-full max-w-302 -ml-80 mt-12"
              height={886}
              imageClassName="object-contain"
              srcKey="landing/pad-pattern.png"
              width={1378}
            />
            <TestimonialsSection background="relative bg-transparent z-10" />
            <Button
              className="flex max-w-fit font-black text-md text-center mx-auto !bg-green-lime !text-black border-4 rounded-full hover:scale-110 duration-100 relative z-20"
              onClick={handleButtonClick}
              to={entryRoute}
            >
              {t('testimonials.buttonCTA')}
            </Button>
          </div>
        </div>
        <div className="overflow-hidden " ref={sectionRefs.mathRecipe}>
          <MathRecipe />
        </div>
        <div
          className="overflow-hidden bg-gradient-to-b from-purple-darker to-purple-nights"
          ref={sectionRefs.baseBuildBurn}
        >
          <BaseBuildBurn />
        </div>
        <div className="overflow-hidden mx-auto" ref={sectionRefs.atomicMath}>
          <AtomicMath />
        </div>
        {!hideFaq && (
          <div ref={sectionRefs.faq}>
            <FAQ isLandingPageFAQ />
          </div>
        )}
        <div ref={sectionRefs.knowledgeGaps}>
          <KnowledgeGaps />
        </div>
      </div>
    </LandingEntryRouteContext.Provider>
  )
}
